import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Badge, Button, Empty, Popover, Tabs, TabsProps, Tooltip } from 'antd';
import Assests from 'assets/icons';
import axios from 'axios';
import RowLoader from 'components/Sceletons/RowLoader';
import { AgreementNotificationsEnum, DocumentNotificationEnum, InviteNotificationsEnum, SigningNotificationsEnum } from 'interfaces/INotification';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { fetchNotificationUnviewed, fetchRoomNotifications } from 'store/reducers/roomsCreator';
import colors from '../../../scss/variables/colors.module.scss';
import NotificationList from './NotificationList';
import classes from './Notifications.module.scss';
import { setIsLoadingNotifications, setNotifications, setUnviewedNotifications } from 'store/slices/dataRoomSlice';
import api from 'api';

const inviteTypes = Object.values(InviteNotificationsEnum);
const documentTypes = Object.values(DocumentNotificationEnum);
export const agreementTypes = Object.values(AgreementNotificationsEnum);
export const signingTypes = Object.values(SigningNotificationsEnum);

type Props = {};

const Notifications: React.FC<Props> = () => {
  const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false);
  const [tabItems, setTabItems] = React.useState<TabsProps['items']>([]);
  const [selectedKeyIdx, setSelectedKeyIdx] = React.useState(0);
  const [updatedNotifications, setUpdatedNotifications] = React.useState<string[]>([]);

  const { notifications, notificationStatus, dataRoom, isLoadingNotifications, notificationsUnviewed } = useAppSelector((state) => state.dataRoom);
  const { userData } = useAppSelector((state) => state.userData);

  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || localStorage.getItem('language') || userData?.language || 'en';
  const showNotificationsCount = notificationsUnviewed === 0 || isNotificationsOpen;

  React.useEffect(() => {
    const source = axios.CancelToken.source();
    dispatch(fetchRoomNotifications({ locale, source }));
    return () => {
      source.cancel();
    }
  }, [locale]);

  
  React.useEffect(() => {
    //fallback to english for spanish locale
    if (
      locale === 'es' && 
      notifications.length === 0 && 
      (notificationStatus === 'empty' || notificationStatus === 'fulfilled')
    ) {
      const source = axios.CancelToken.source();
      dispatch(fetchRoomNotifications({ locale: 'en', source }));
    }
  }, [notifications, locale, notificationStatus]);

  React.useEffect(() => {
    userData && dispatch(fetchNotificationUnviewed({}))
  }, [userData?.id]);

  React.useEffect(() => {
    if (notifications.length) {
      const itemsFilters = [
        {
          key: 'all',
          children: notifications,
          isDisabled: false,
        },
        {
          key: 'currentRoom',
          filters: `[{"field":"details.room_id","value":"${dataRoom?.id}"}]`,
          children: [notifications[0]],
          isDisabled: !dataRoom?.id,
        },
        {
          key: 'documents',
          filters: `[{"field":"type","value": ${JSON.stringify(documentTypes)}}]`,
          children: [notifications[0]],
          isDisabled: !Boolean(notifications[0]),
        },
        {
          key: 'invites',
          filters: `[{"field":"type","value": ${JSON.stringify(inviteTypes)}}]`,
          children: [notifications[0]],
          isDisabled: !Boolean(notifications[0]),
        },
        {
          key: 'agreements',
          filters: `[{"field":"type","value": ${JSON.stringify(agreementTypes)}}]`,
          children: [notifications[0]],
          isDisabled: !Boolean(notifications[0]),
        },
        {
          key: 'signings',
          filters: `[{"field":"type","value": ${JSON.stringify(signingTypes)}}]`,
          children: [notifications[0]],
          isDisabled: !Boolean(notifications[0]),
        },
      ];      

      const items: TabsProps['items'] = itemsFilters.map((item) => ({
        ...item,
        disabled: item.isDisabled,
        label: t(`Header.Notification.tabs.${item.key}`),
        children: (
          <NotificationList
            notifications={item.children}
            currentKey={item.key}
            setIsNotificationsOpen={setIsNotificationsOpen}
            filters={item.filters}
            setUpdatedNotifications={setUpdatedNotifications}
            updatedNotifications={updatedNotifications}
          />
        ),
      }));

      setTabItems(items);
    }
  }, [notifications, dataRoom?.id, isLoadingNotifications, updatedNotifications]);


  const onViewBtnClick = async () => {    
    dispatch(setIsLoadingNotifications(true));
    try {
      await api.viewAllNotifications(userData?.id!, notifications[0].created_at);
      dispatch(setNotifications(notifications.map(notif => ({...notif, status: 1}))));
      dispatch(setUnviewedNotifications(0));
    } catch (e) {

    } finally {
      dispatch(setIsLoadingNotifications(false))
    }
  };

  const onChange = (activeKey: string) => setSelectedKeyIdx(tabItems?.findIndex((tab) => tab.key === activeKey)!);
  const onLeftClick = () => setSelectedKeyIdx((prev) => (prev !== 0 ? prev - 1 : 0));
  const onRightClick = () =>
    setSelectedKeyIdx((prev) => (prev !== tabItems?.length! - 1 ? prev + 1 : tabItems?.length! - 1));

  const notificationsCases = {
    pending: [1, 2, 3, 4].map((_, idx) => (
      <RowLoader
        padding={'10px 12px 0 12px'}
        width={400}
        height={100}
        key={`load-${idx}`}
      />
    )),
    fulfilled: (
      <>
        <div className={classes.titleWrap}>
          <div className={classes.title}>
            {t('Header.Notification.mainTitle')}
          </div>
          {selectedKeyIdx === 0 && (
            <Button
              loading={isLoadingNotifications}
              type='text'
              className={classes.viewAllBtn}
              onClick={onViewBtnClick}
            >
              {t('Header.Notification.btn')}
            </Button>
          )}
        </div>
        {notifications[0]
          ? <Tabs
            items={tabItems}
            activeKey={ tabItems?.length ? tabItems[selectedKeyIdx].key : undefined }
            onChange={onChange}
            tabBarExtraContent={{
              left: (
                <Button type='text' className={classes.navBtn} onClick={onLeftClick}>
                  <LeftOutlined className={classes.leftIcon} />
                </Button>
              ),
              right: (
                <Button type='text' className={classes.navBtn} onClick={onRightClick}>
                  <RightOutlined className={classes.rightIcon} />
                </Button>
              ),
            }}
            more={{ icon: null, visible: false }}
          />
          : <Empty description={t('Header.Notification.empty')} />
        }

      </>
    ),
    empty: <Empty description={t('Header.Notification.empty')} />,
  };
  
  return (
    <Popover
      content={notificationsCases[notificationStatus]}
      trigger='click'
      rootClassName={classes.notification}
      styles={{ root: { maxWidth: 420} }}
      onOpenChange={setIsNotificationsOpen}
      open={isNotificationsOpen}
    >
      <Badge
        color={colors.primary100}
        count={!showNotificationsCount ? notificationsUnviewed : undefined}
        overflowCount={99}
        offset={[-10, 10]}
        size='small'
        style={{ fontSize: 12, fontWeight: 500, maxWidth: 25 }}
      >
        <Tooltip placement='right' title={t('sidebar.notifications')}>
          <Button
            className={classes.notificationBell}
            type='text'
            size='large'
            icon={<Assests.BellIcon />}
          />
        </Tooltip>
      </Badge>
    </Popover>
  );
};

export default Notifications;
